import clsx from 'clsx';
import { Crosshair, Mic, Skull, UserX, WifiOff } from 'lucide-react';
import { ROLE_INFO, isMafiaTeam, type PublicPlayer } from '@mafia/core';
import { Avatar } from '@/components/Avatar';

export type Mark = 'kill' | 'heal' | 'check' | 'vote' | 'suspect' | 'visit' | 'pending';

export interface CardInfo {
  isMe: boolean;
  /** Своя роль скрыта от соседей по столу (переключатель «Моя роль»). */
  hideRole: boolean;
  /** Аватарка из лобби (в игровую проекцию не входит). */
  avatarId: string | null;
  speaking: boolean;
  /** Ночь с любовницей: сегодня молчит и не голосует. */
  silenced: boolean;
  nominated: boolean;
  candidate: boolean;
  /** Число голосов (только при открытом голосовании). */
  votes: number | null;
  voters: number[];
  /** Места мафиози, целящихся в этого игрока (видно только мафии). */
  mafiaVoters: number[];
  /** Результат моей проверки: true — мафия/шериф. */
  check: { positive: boolean; kind: 'SHERIFF' | 'DON' } | null;
  marks: Mark[];
  /** Ведущий может удалить игрока из партии. */
  kickable?: boolean;
  onKick?: () => void;
  /** Подпись для ведущего: ночной ход игрока. */
  note?: string;
}

const DEATH_TEXT = { NIGHT_KILL: 'Убит ночью', VOTE: 'Исключён', LEFT: 'Вышел' } as const;

export function PlayerCard({ p, info, selectable, onClick }: { p: PublicPlayer; info: CardInfo; selectable: boolean; onClick?: () => void }) {
  const dead = !p.alive;
  const m = new Set(info.marks);
  const ring = m.has('pending')
    ? 'ring-2 ring-gold-400 ring-offset-2 ring-offset-ink-950'
    : m.has('kill')
      ? 'ring-2 ring-blood-500'
      : m.has('heal')
        ? 'ring-2 ring-emerald-400'
        : m.has('check')
          ? 'ring-2 ring-sky-400'
          : m.has('visit')
            ? 'ring-2 ring-pink-400'
          : m.has('vote')
            ? 'ring-2 ring-gold-400'
            : m.has('suspect')
              ? 'ring-2 ring-purple-400/70'
              : '';

  return (
    <div className="relative">
    <button
      type="button"
      onClick={selectable ? onClick : undefined}
      disabled={!selectable}
      aria-pressed={info.marks.length > 0}
      aria-label={`Игрок №${p.seat} ${p.nickname}${dead ? ', выбыл' : ''}`}
      className={clsx(
        'relative flex h-full w-full flex-col items-center gap-1.5 rounded-2xl border px-2 pb-2.5 pt-3 text-center transition',
        'border-white/5 bg-ink-900/80',
        info.isMe && 'border-gold-400/30',
        info.speaking && 'animate-speaking border-gold-400/60 bg-gold-400/5',
        info.candidate && !dead && 'border-blood-500/40 bg-blood-600/5',
        selectable && 'cursor-pointer hover:-translate-y-0.5 hover:border-white/20 hover:bg-ink-800',
        !selectable && 'cursor-default',
        dead && 'opacity-45 grayscale',
        ring,
      )}
    >
      <span className="absolute left-2 top-1.5 font-display text-lg font-bold text-zinc-500">{p.seat}</span>
      {!p.connected && !dead && <WifiOff className="absolute right-2 top-2 h-3.5 w-3.5 text-gold-300" aria-label="Не в сети" />}
      {info.speaking && <Mic className="absolute right-2 top-2 h-4 w-4 text-gold-400" aria-label="Говорит" />}

      <div className="relative">
        <Avatar id={info.avatarId} name={p.nickname} size={44} className={clsx(dead && 'opacity-60')} />
        {dead && <Skull className="absolute -bottom-1 -right-1 h-5 w-5 rounded-full bg-ink-950 p-0.5 text-zinc-300" />}
      </div>
      <span className="w-full truncate text-sm font-medium">
        {p.nickname}
        {info.isMe && <span className="text-gold-300"> · вы</span>}
      </span>

      <div className="flex min-h-[1.25rem] flex-wrap items-center justify-center gap-1">
        {p.role && !info.hideRole && (
          <span
            className={clsx(
              'rounded-md px-1.5 py-0.5 text-[11px]',
              isMafiaTeam(p.role) ? 'bg-blood-600/20 text-blood-400' : p.role === 'MANIAC' ? 'bg-purple-500/20 text-purple-300' : 'bg-indigo-500/20 text-indigo-300',
            )}
          >
            {ROLE_INFO[p.role].emoji} {ROLE_INFO[p.role].name}
          </span>
        )}
        {dead && p.deathReason && <span className="text-[11px] text-zinc-500">{DEATH_TEXT[p.deathReason]}</span>}
        {info.silenced && !dead && <span className="rounded-md bg-pink-500/15 px-1.5 py-0.5 text-[11px] text-pink-300">🤐 молчит</span>}
        {info.nominated && !dead && <span className="rounded-md bg-blood-600/20 px-1.5 py-0.5 text-[11px] text-blood-400">выставлен</span>}
        {info.check && (
          <span
            className={clsx(
              'rounded-md px-1.5 py-0.5 text-[11px]',
              info.check.positive ? 'bg-blood-600/25 text-blood-400' : 'bg-emerald-500/15 text-emerald-300',
            )}
          >
            {info.check.kind === 'SHERIFF' ? (info.check.positive ? 'мафия' : 'не мафия') : info.check.positive ? 'шериф' : 'не шериф'}
          </span>
        )}
      </div>

      {info.note && <span className="text-[11px] leading-tight text-zinc-400">{info.note}</span>}
      {info.mafiaVoters.length > 0 && (
        <div className="absolute -top-2 left-1/2 flex -translate-x-1/2 gap-0.5" title="Сюда целится мафия">
          {info.mafiaVoters.map((s) => (
            <span key={s} className="flex items-center gap-0.5 rounded-full bg-blood-600 px-1.5 text-[10px] font-bold leading-4 text-white">
              <Crosshair className="h-2.5 w-2.5" />
              {s}
            </span>
          ))}
        </div>
      )}
      {info.votes !== null && info.candidate && (
        <span
          className="absolute -top-2 right-2 rounded-full bg-gold-400 px-2 text-xs font-bold leading-5 text-ink-950"
          title={info.voters.length ? `Голоса: ${info.voters.map((v) => `№${v}`).join(', ')}` : 'Нет голосов'}
        >
          {info.votes}
        </span>
      )}
    </button>
      {info.kickable && (
        <button
          type="button"
          onClick={info.onKick}
          className="absolute bottom-1.5 right-1.5 rounded-md p-1 text-zinc-600 hover:bg-blood-600/15 hover:text-blood-400"
          title="Удалить из игры"
          aria-label={`Удалить игрока №${p.seat} из игры`}
        >
          <UserX className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}
